/* Lucian UX/UI Prompt #008 · MIT License (c) 2026 Seokwoooo · https://lucianlabs.dev/uxui-prompt/#008 */
import { useEffect, useRef, useState } from 'react';
import './lucian-008.css';

// formats: [{ key, label, em, cmd, b, full }] — 탭 상태는 React로 올리고 DOM 쓰기는 하지 않는다
export default function LucianCopyFormat({ formats, onCopied }) {
  const rootRef = useRef(null);
  const cmdRef = useRef(null);
  const timer = useRef(0);
  const [cur, setCur] = useState(0);
  const [tip, setTip] = useState('');
  const tabRefs = useRef([]);
  const f = formats[Math.min(cur, formats.length - 1)];

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { setCur(0); }, [formats]);

  const select = (i, focus) => {
    setCur(i);
    if (focus) tabRefs.current[i]?.focus();
    const rm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (cmdRef.current?.animate && !rm) {
      cmdRef.current.animate([{ transform: 'translateY(-60%)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 360, easing: 'cubic-bezier(.34,1.45,.64,1)' });
    }
  };

  const copy = () => {
    const root = rootRef.current;
    const done = ok => {
      onCopied && onCopied(ok, f);
      if (!ok) return;
      clearTimeout(timer.current);
      root.classList.remove('is-copied');
      void root.offsetWidth;
      root.classList.add('is-copied');
      setTip('복사됨');
      timer.current = setTimeout(() => { root.classList.remove('is-copied'); setTip(''); }, 1800);
    };
    try {
      navigator.clipboard.writeText(f.full).then(() => done(true), () => done(false));
    } catch { done(false); }
  };

  if (!f) return null;
  return (
    <div ref={rootRef} className="ux u008 u008-modal">
      <div className="u008-tabs" role="tablist" aria-label="복사 형식">
        {formats.map((x, i) => (
          <button
            key={x.key}
            ref={el => { tabRefs.current[i] = el; }}
            className="u008-tab" type="button" role="tab"
            aria-selected={i === cur} tabIndex={i === cur ? 0 : -1}
            onClick={() => select(i)}
            onKeyDown={e => {
              const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
              if (d) { e.preventDefault(); select((i + d + formats.length) % formats.length, true); }
            }}
          >{x.label}</button>
        ))}
      </div>
      <div className="u008-box">
        <code className="u008-code" title={f.full}><em aria-hidden="true">{f.em}</em><span ref={cmdRef} className="u008-cmd">{f.cmd}</span>{f.b && <b>{f.b}</b>}</code>
        <button className="u008-btn" type="button" aria-label={`${f.label} 복사`} onClick={copy}>
          <svg className="u008-cp" viewBox="0 0 24 24" aria-hidden="true"><rect x="8.5" y="8.5" width="11" height="11" rx="2.5"/><path d="M15.5 8.5V6.5a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h2"/></svg>
          <svg className="u008-ok" viewBox="0 0 24 24" aria-hidden="true"><path d="M5.5 12.5l4.2 4.2L18.5 8"/></svg>
        </button>
        <span className="u008-tip" role="status">{tip}</span>
      </div>
    </div>
  );
}
